import React from 'react';
import { ExternalLink, Ban, ChevronRight, Eye, Bookmark } from 'lucide-react';
import { CodeNameTag } from './CodeNameTag';
import { fmtRevenue1, fmtInt } from '../lib/fieldInfo';

export const scoreColor = (s) => (s >= 85 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40'
  : s >= 60 ? 'text-sky-400 bg-sky-500/10 border-sky-500/40'
  : s >= 35 ? 'text-amber-400 bg-amber-500/10 border-amber-500/40'
  : 'text-muted-foreground/70 bg-secondary border-border');

const websiteUrl = (w) => (w.startsWith('http') ? w : `https://${w}`);

export function CompanyRow({ r, i, score, reason, badge, viewedSet, onOpen, onFlag, onPassClick, prefix, bookmarkedSet, onBookmark }) {
  const bookmarked = bookmarkedSet?.has(r.id);
  return (
    <div onClick={() => onOpen(r.id)} data-testid={`${prefix}-row-${r.id}`}
      className="px-4 py-3 border-b border-border/40 even:bg-secondary/40 row-hover-glow cursor-pointer flex items-start gap-3">
      <div className="shrink-0 flex flex-col items-center gap-1 w-12">
        <span className="text-[10px] text-muted-foreground/50 font-mono">#{i + 1}</span>
        <span className={`text-sm font-mono font-bold rounded px-1.5 py-0.5 border ${scoreColor(score || 0)}`}
          data-testid={`${prefix}-score-${r.id}`}>{score != null ? score : '—'}</span>
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {viewedSet.has(r.id) && <Eye className="w-3.5 h-3.5 text-blue-400/70 shrink-0" title="Bereits angesehen" />}
          <span className="text-sm text-foreground font-medium">{r.name}</span>
          {r.random_project_name && <CodeNameTag name={r.random_project_name} className="text-[10px] text-blue-400/70 font-mono" />}
          {r.pipeline_stage === 'passed' && <span className="text-[9px] font-bold text-red-400 bg-red-500/10 border border-red-500/40 rounded px-1 py-0.5">PASSED</span>}
          {r.ac_flagged && <span className="text-[9px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/40 rounded px-1 py-0.5">! GEMERKT</span>}
        </div>
        {badge}
        {reason && <p className="text-[11px] text-violet-300/90 italic leading-snug" data-testid={`${prefix}-reason-${r.id}`}>{reason}</p>}
        <p className="text-[11px] text-muted-foreground truncate">{[r.sector, r.subsector].filter(Boolean).join(' · ')}</p>
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground font-mono">
          {r.revenue && <span>Umsatz {fmtRevenue1(r.revenue)} €m</span>}
          {r.ebitda && <span>EBITDA {fmtRevenue1(r.ebitda)} €m</span>}
          {r.employees && <span>{fmtInt(r.employees)} MA</span>}
          {r.founded && <span>gegr. {r.founded}</span>}
          {r.headquarters && <span className="truncate max-w-[180px]">{r.headquarters}</span>}
          {r.succession_score != null && (
            <span className="text-muted-foreground/60">
              Succession {r.succession_score}/100
              {r.ai_quick_scored && <span className="ml-1 text-[9px] font-bold text-sky-400 bg-sky-500/10 border border-sky-500/40 rounded px-1 py-px font-sans">KI</span>}
            </span>
          )}
          {r.website && (
            <a href={websiteUrl(r.website)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
              data-testid={`${prefix}-website-${r.id}`}
              className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 font-sans">
              <ExternalLink className="w-3 h-3" /> {r.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1 pt-1">
        <button onClick={(e) => onBookmark(e, r)} data-testid={`${prefix}-bookmark-btn-${r.id}`}
          title={bookmarked ? 'Von persönlicher Merkliste entfernen' : 'Bookmarken — auf deine persönliche Merkliste'}
          className={`w-7 h-7 rounded-sm border transition-colors inline-flex items-center justify-center ${bookmarked
            ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
            : 'border-border text-muted-foreground/40 hover:text-violet-300 hover:border-violet-500/40'}`}>
          <Bookmark className="w-3.5 h-3.5" fill={bookmarked ? 'currentColor' : 'none'} />
        </button>
        <button onClick={(e) => onFlag(e, r)} data-testid={`${prefix}-flag-btn-${r.id}`}
          title={r.ac_flagged ? 'Markierung (!) entfernen' : 'Merken — mit „!" fürs AC markieren'}
          className={`w-7 h-7 rounded-sm border text-sm font-bold transition-colors ${r.ac_flagged
            ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
            : 'border-border text-muted-foreground/40 hover:text-amber-400 hover:border-amber-500/40'}`}>!</button>
        {r.pipeline_stage !== 'passed' && (
          <button onClick={(e) => { e.stopPropagation(); onPassClick(r); }}
            data-testid={`${prefix}-pass-btn-${r.id}`} title="Pass — direkt aussortieren"
            className="w-7 h-7 rounded-sm border border-border text-muted-foreground/40 hover:text-red-400 hover:border-red-500/40 transition-colors inline-flex items-center justify-center">
            <Ban className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20" />
      </div>
    </div>
  );
}
